import React, { useEffect, useState } from 'react';

export default function ResumeGate({ token, onAuthorized }) {
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadSession() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`/api/intake-draft-session?t=${encodeURIComponent(token)}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Saved form lookup failed (${res.status}).`);
        if (!cancelled) setSession(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadSession();
    return () => { cancelled = true; };
  }, [token]);

  async function sendCode(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/intake-draft-send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Code send failed (${res.status}).`);
      setStep('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function verifyCode(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/intake-draft-verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Code verification failed (${res.status}).`);
      onAuthorized(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={s.container}>
      <div style={s.header}>
        <img src="/logo.png" alt="Translating Insurance" style={s.logoImg} />
      </div>
      <div style={s.titleBar}>RESUME SAVED FORM</div>
      <div style={s.card}>
        {loading ? (
          <>
            <h2 style={s.heading}>Checking Saved Form</h2>
            <p style={s.copy}>One moment while we verify this saved form link.</p>
          </>
        ) : (
          <>
            <h2 style={s.heading}>Verify Your Email</h2>
            <p style={s.copy}>
              Enter the email address used for this saved form. We will send a short verification code before the form reloads.
            </p>
            {session?.maskedEmail && (
              <div style={s.hint}>Saved email: {session.maskedEmail}</div>
            )}

            {step === 'email' ? (
              <form onSubmit={sendCode} style={s.form}>
                <label style={s.label} htmlFor="resume-email">Email Address</label>
                <input
                  id="resume-email"
                  type="email"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  style={s.input}
                  autoComplete="email"
                  required
                />
                <button type="submit" style={s.btn} disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send Code'}
                </button>
              </form>
            ) : (
              <form onSubmit={verifyCode} style={s.form}>
                <label style={s.label} htmlFor="resume-code">Verification Code</label>
                <input
                  id="resume-code"
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ ...s.input, letterSpacing: 4, fontWeight: 700 }}
                  autoComplete="one-time-code"
                  required
                />
                <button type="submit" style={s.btn} disabled={submitting || code.length !== 6}>
                  {submitting ? 'Verifying...' : 'Continue'}
                </button>
                <button type="button" style={s.linkBtn} onClick={() => setStep('email')} disabled={submitting}>
                  Use a different email
                </button>
              </form>
            )}
          </>
        )}
        {error && <div style={s.error}>{error}</div>}
      </div>
    </div>
  );
}

const s = {
  container: {
    width: '100%',
    maxWidth: 620,
    margin: '0 auto',
    paddingTop: 32,
  },
  header: {
    textAlign: 'center',
    marginBottom: 12,
  },
  logoImg: {
    maxWidth: 340,
    width: '100%',
    height: 'auto',
  },
  titleBar: {
    background: '#1a3364',
    borderRadius: 8,
    padding: '13px 24px',
    textAlign: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: '0.08em',
    marginBottom: 24,
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '28px 32px',
    boxShadow: '0 2px 16px rgba(26,51,100,0.10)',
  },
  heading: {
    color: '#1a3364',
    marginTop: 0,
    marginBottom: 10,
  },
  copy: {
    color: '#4a6288',
    lineHeight: 1.6,
    marginTop: 0,
  },
  hint: {
    background: '#eef6fb',
    border: '1px solid #c7e3f2',
    borderRadius: 8,
    color: '#1a3364',
    padding: '10px 12px',
    fontSize: 14,
    marginBottom: 18,
  },
  form: {
    display: 'grid',
    gap: 12,
  },
  label: {
    color: '#1a3364',
    fontWeight: 700,
    fontSize: 14,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #b8cfe8',
    borderRadius: 8,
    padding: '12px 14px',
    fontSize: 16,
    color: '#17305f',
  },
  btn: {
    padding: '12px 32px',
    background: '#1a3364',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  linkBtn: {
    border: 'none',
    background: 'transparent',
    color: '#1a3364',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 8,
  },
  error: {
    color: '#c0392b',
    background: '#fdf0ef',
    border: '1px solid #f5c6c2',
    borderRadius: 8,
    padding: '10px 12px',
    marginTop: 16,
    fontSize: 14,
  },
};
